/**
 * Décompresse une chaîne encodée en base64 avec l'algorithme LZ-String
 * (identique à `LZString.decompressFromBase64`).
 * Implémenté localement pour éviter une dépendance directe sur lz-string
 * qui est une dep transitive non déclarée dans package.json.
 */

const KEY_STR_BASE64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=';

function decompressFromBase64(input: string): string | null {
  if (!input) return null;
  return decompress(input.length, 32, (index: number) =>
    KEY_STR_BASE64.indexOf(input[index] ?? ''),
  );
}

function decompress(
  length: number,
  resetValue: number,
  getNextValue: (i: number) => number,
): string | null {
  const result: string[] = [];
  const dictionary: string[] = [];
  let enlargeIn = 4;
  let dictSize = 4;
  let numBits = 3;
  let entry = '';
  let w = '';
  let bits = 0;
  let maxpower = Math.pow(2, 2);
  let power = 1;
  let c: string | undefined;
  let data_val = getNextValue(0);
  let data_index = 1;
  let data_position = resetValue;

  for (let i = 0; i < 3; i++) dictionary[i] = String.fromCharCode(i);

  while (power !== maxpower) {
    const resb = data_val & data_position;
    data_position >>= 1;
    if (data_position === 0) {
      data_position = resetValue;
      data_val = getNextValue(data_index++);
    }
    bits |= (resb > 0 ? 1 : 0) * power;
    power <<= 1;
  }

  const next = bits;
  switch (next) {
    case 0:
      bits = 0;
      maxpower = Math.pow(2, 8);
      power = 1;
      while (power !== maxpower) {
        const resb = data_val & data_position;
        data_position >>= 1;
        if (data_position === 0) {
          data_position = resetValue;
          data_val = getNextValue(data_index++);
        }
        bits |= (resb > 0 ? 1 : 0) * power;
        power <<= 1;
      }
      c = String.fromCharCode(bits);
      break;
    case 1:
      bits = 0;
      maxpower = Math.pow(2, 16);
      power = 1;
      while (power !== maxpower) {
        const resb = data_val & data_position;
        data_position >>= 1;
        if (data_position === 0) {
          data_position = resetValue;
          data_val = getNextValue(data_index++);
        }
        bits |= (resb > 0 ? 1 : 0) * power;
        power <<= 1;
      }
      c = String.fromCharCode(bits);
      break;
    case 2:
      return '';
    default:
      return null;
  }

  dictionary[3] = c;
  w = c;
  result.push(c);

  while (true) {
    if (data_index > length) return '';

    bits = 0;
    maxpower = Math.pow(2, numBits);
    power = 1;
    while (power !== maxpower) {
      const resb = data_val & data_position;
      data_position >>= 1;
      if (data_position === 0) {
        data_position = resetValue;
        data_val = getNextValue(data_index++);
      }
      bits |= (resb > 0 ? 1 : 0) * power;
      power <<= 1;
    }

    const cc = bits;
    switch (cc) {
      case 0:
        bits = 0;
        maxpower = Math.pow(2, 8);
        power = 1;
        while (power !== maxpower) {
          const resb = data_val & data_position;
          data_position >>= 1;
          if (data_position === 0) {
            data_position = resetValue;
            data_val = getNextValue(data_index++);
          }
          bits |= (resb > 0 ? 1 : 0) * power;
          power <<= 1;
        }
        dictionary[dictSize++] = String.fromCharCode(bits);
        if (--enlargeIn === 0) {
          enlargeIn = Math.pow(2, numBits++);
        }
        break;
      case 1:
        bits = 0;
        maxpower = Math.pow(2, 16);
        power = 1;
        while (power !== maxpower) {
          const resb = data_val & data_position;
          data_position >>= 1;
          if (data_position === 0) {
            data_position = resetValue;
            data_val = getNextValue(data_index++);
          }
          bits |= (resb > 0 ? 1 : 0) * power;
          power <<= 1;
        }
        dictionary[dictSize++] = String.fromCharCode(bits);
        if (--enlargeIn === 0) {
          enlargeIn = Math.pow(2, numBits++);
        }
        break;
      case 2:
        return result.join('');
    }

    if (cc === 2) break;

    entry =
      cc < dictSize ? (dictionary[cc] ?? '') : ((cc === dictSize ? w + (w[0] ?? '') : null) ?? '');
    if (entry === null) return null;
    result.push(entry);

    dictionary[dictSize++] = w + (entry[0] ?? '');
    if (--enlargeIn === 0) {
      enlargeIn = Math.pow(2, numBits++);
    }
    w = entry;
  }

  return result.join('');
}

/**
 * Parse le format Obsidian `.excalidraw.md` et retourne le JSON Excalidraw
 * attendu par `ExcalidrawEditor`. Retourne `null` si le parsing échoue.
 */
export function parseExcalidrawMd(content: string): string | null {
  try {
    const match = /```compressed-json\s*([\s\S]*?)```/.exec(content);
    if (!match?.[1]) return null;

    const compressed = match[1].trim();
    const decompressed = decompressFromBase64(compressed);
    if (!decompressed) return null;

    JSON.parse(decompressed);
    return decompressed;
  } catch {
    return null;
  }
}
